import type { BookDefaultStylesV1 } from './bookDefaults';
import type { LineOverrideLike } from './bookStyleCss';
import { extractShlokaLineTag } from './bidhiEditByTag';
import { FORM_STYLE_FIELD_KEYS } from './lineStyleForm';

export type TagLineStyleOverride = LineOverrideLike & { span?: 12 | 24 };

export function mergeLineOverrides(
  ...parts: (LineOverrideLike | undefined | null)[]
): LineOverrideLike | undefined {
  let merged: LineOverrideLike = {};
  for (const part of parts) {
    if (part && typeof part === 'object') {
      merged = { ...merged, ...part };
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function getGlobalTagLineOverride(
  bookDefaults: BookDefaultStylesV1 | undefined,
  tagName: string | undefined,
): TagLineStyleOverride | undefined {
  if (!tagName || !bookDefaults?.tagLineStyles) return undefined;
  const style = bookDefaults.tagLineStyles[tagName];
  return style && typeof style === 'object' ? (style as TagLineStyleOverride) : undefined;
}

/** Merge template-level tag style with per-line / per-book overrides (per-book wins). */
export function mergeGlobalTagStyle(
  bookDefaults: BookDefaultStylesV1 | undefined,
  metadataTag: string | undefined,
  ...parts: (LineOverrideLike | undefined | null)[]
): LineOverrideLike | undefined {
  return mergeLineOverrides(
    bookDefaults?.defaultLineStyle,
    getGlobalTagLineOverride(bookDefaults, metadataTag),
    ...parts,
  );
}

export function resolveLineSpan(
  bookDefaults: BookDefaultStylesV1 | undefined,
  metadataTag: string | undefined,
  lineId: number | undefined,
  blockLayoutOverrides?: Record<number, { span?: 12 | 24 }>,
): 12 | 24 | undefined {
  const perBook = lineId != null ? blockLayoutOverrides?.[lineId]?.span : undefined;
  if (perBook === 12 || perBook === 24) return perBook;
  const globalSpan = getGlobalTagLineOverride(bookDefaults, metadataTag)?.span;
  if (globalSpan === 12 || globalSpan === 24) return globalSpan;
  return undefined;
}

export function getGranthaReferenceTagOverride(
  bookDefaults: BookDefaultStylesV1 | undefined,
  granthaRef: { references?: unknown[] } | undefined,
): TagLineStyleOverride | undefined {
  if (!bookDefaults?.tagLineStyles || !granthaRef) return undefined;
  for (const ref of granthaRef.references || []) {
    for (const sl of (ref as { shlokaLines?: unknown[] }).shlokaLines || []) {
      const tag = extractShlokaLineTag(sl);
      const style = tag ? bookDefaults.tagLineStyles[tag] : undefined;
      if (style && typeof style === 'object') return style as TagLineStyleOverride;
    }
  }
  return undefined;
}

function cleanTagLineStyleEntry(raw: unknown): TagLineStyleOverride | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: TagLineStyleOverride = {};
  for (const key of FORM_STYLE_FIELD_KEYS) {
    const v = (raw as Record<string, unknown>)[key];
    if (v !== undefined && v !== null && v !== '') {
      (out as Record<string, unknown>)[key] = v;
    }
  }
  const span = (raw as { span?: unknown }).span;
  if (span === 12 || span === 24) out.span = span;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Normalize stored tag style map from API / form. */
export function cleanTagLineStyles(
  raw: unknown,
): Record<string, TagLineStyleOverride> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, TagLineStyleOverride> = {};
  for (const [tag, style] of Object.entries(raw as Record<string, unknown>)) {
    const name = String(tag ?? '').trim();
    if (!name) continue;
    const cleaned = cleanTagLineStyleEntry(style);
    if (cleaned && Object.keys(cleaned).length > 0) {
      out[name] = cleaned;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
